import React, { useEffect } from 'react'
import DocumentTitle from 'components/TopLevel/DocumentTitle'
import { useDispatch, useSelector } from 'react-redux'

import { Paths } from 'utils/paths'
import LearnGuides from 'actions/learn_guides'
import { LearnGuidesSelectors } from 'reducers/learn_guides'
import IOLink from 'components/Elements/IOLink'
import ExternalLink from 'components/Elements/ExternalLink'
import GuideActions from 'actions/guides'
import WelcomeActions from 'actions/welcome'
import Navigation from 'utils/navigation'

import Video from './video'
import Image from './image'
import TwoColumnContent from './TwoColumnContent'
import OneColumnContent from './OneColumnContent'

import book from 'images/landing/book.svg'
import card from 'images/landing/card.svg'
import chart from 'images/landing/chart.svg'
import chat from 'images/landing/chat.svg'
import chip from 'images/landing/chip.svg'
import download from 'images/landing/download.svg'
import gauge from 'images/landing/gauge.svg'
import interact from 'images/landing/interact.svg'
import links from 'images/landing/links.svg'
import puzzle from 'images/landing/puzzle.svg'
import secure from 'images/landing/secure.svg'
import shield from 'images/landing/shield.svg'
import wippersnapper from 'images/landing/wippersnapper.svg'

import io_plus_logo_white from 'images/io-plus-logo-white.svg'
import io_logo_black from 'images/io-logo-black.svg'

import 'stylesheets/landing.scss'

const Landing = () => {
  const
    dispatch = useDispatch(),
    user = useSelector(state => state.session.user),
    featuredGuides = useSelector(state => LearnGuidesSelectors.getFeaturedGuides(state)),
    guides = featuredGuides || [],
    [guide1, guide2, guide3, guide4] = guides

  useEffect(() => {
    dispatch(LearnGuides.getFeatured())
  }, [dispatch])

  const handleGetStarted = async () => {
    if (user) {
      await dispatch(WelcomeActions.dismiss())
      Navigation.go(Paths.overview({ username: user.username }))
    }
  }

  const handleUpgradeClick = async () => {
    if (user) {
      await dispatch(WelcomeActions.dismiss())
      Navigation.go(Paths.my_plan({ username: user.username }))
    }
  }

  const freeSignUpLink = () => {
    return user ? (
      <button onClick={handleGetStarted} className='btn'>
        Get Started
      </button>
    ) : (
      <a href={import.meta.env.VITE_IO_SIGN_UP_URL} className='btn'>
        Sign Up Now
      </a>
    )
  }

  const plusSignUpLink = () => {
    return user ? (
      <button onClick={handleUpgradeClick} className='btn'>
        Upgrade to IO+
      </button>
    ) : (
      <a href={import.meta.env.VITE_IO_SIGN_UP_URL} className='btn'>
        Sign Up Now
      </a>
    )
  }

  const showGuides = () => {
    dispatch(GuideActions.showGuide('meta'))
  }

  return (
    <div id="landing-page" >
      <DocumentTitle title="Welcome to Adafruit IO" />
      <TwoColumnContent
        textContent={
          <>
            <h1>The Internet of Things for <span className="everyone">Everyone</span></h1>
            <div className="tagline">
              <p>The easiest way to stream, log, and interact with your data.</p>
            </div>
            <div className="get-started">
              {freeSignUpLink()}
            </div>
          </>
        }
        media={<Video />}
        firstRow={true}
        mediaFirst={false}
      />
      <TwoColumnContent
        textContent={
          <>
            <h2>What exactly is Adafruit IO?</h2>
            <div className="tagline">
              <p>
                Adafruit IO is a cloud service by Adafruit for building Internet of Things (IoT) projects. It lets users connect, monitor, and control IoT devices and sensors online without writing a single line of code.
              </p>
              <p>
                Adafruit IO also offers resources and tutorials to help users start their IoT projects. Including libraries for languages like CircuitPython, Arduino, Python, and more, making device integration straightforward. With its user-friendly interface and extensive documentation, Adafruit IO is the easiest way to stream, log, and interact with your data.
              </p>
              <p>
                Adafruit IO is the Internet of Things for Everyone!
              </p>
            </div>
          </>
        }
        media={<Image guide={guide1} />}
        mediaFirst={true}
      />

      <OneColumnContent>
        <section>
          <div>
            <h2>What can Adafruit IO do for you?</h2>
            <p>
              It is probably easier for us to explain what it can’t do for you.
            </p>
          </div>
          <div className="icon-block">
            <div>
              <img alt="Chart Icon"
                src={chart}
                loading="lazy" />
              <div>
                <h3>Display Your Data in Real-Time</h3>
                <p>
                  View your raw data as it is being sent by your device, or put it into a fancy dashboard to view it in a graph, gauge, map, or more!
                </p>
              </div>
            </div>
            <div>
              <img alt="Interact Icon"
                src={interact}
                loading="lazy" />
              <div>
                <h3>Interact With Your Data</h3>
                <p>
                  Viewing your data is fun, but it is even more fun to interact with your data in real-time. Control motors, turn lights on and off, adjust a temperature....you are really only limited by your imagination.
                </p>
              </div>
            </div>
            <div>
              <img alt="Puzzle Icon"
                src={puzzle}
                loading="lazy" />
              <div>
                <h3>Blockly Actions</h3>
                <p>
                  Our Blockly-based actions system allows you to easily create complex actions that trigger automatically. Send an email or SMS message* if a temperature drops below a certain threshold, change the color of a light to remind you to grab an umbrella if it is going to rain, etc.
                </p>
              </div>
            </div>
            <div>
              <img alt="Links Icon"
                src={links}
                loading="lazy" />
              <div>
                <h3>Easy Web Service Connections</h3>
                <p>
                  Adafruit IO makes it super easy to connect to other web services like Slack, Mastodon, Discord, RSS Feeds, Weather Services*, and more!
                </p>
              </div>
            </div>
          </div>
        </section>
      </OneColumnContent>

      <TwoColumnContent
        textContent={
          <>
            <h2>Unleash the power of Adafruit IO without writing a line of code</h2>
            <div className="tagline">
              <p>
                With Adafruit IO, you can connect your devices without writing a single line of code using our custom WipperSnapper firmware. Load the firmware onto your <IOLink to="/works-with-wippersnapper">compatible board</IOLink>, add credentials, and plug it into power. Your board will automatically register itself with your Adafruit IO account.
              </p>
              <p>
                From there, you can add components to your board such as buttons, switches, potentiometers, sensors, and more! Components are dynamically added to hardware, so you can immediately start interacting, logging, and streaming the data your projects produce without writing code.
              </p>
            </div>
          </>
        }
        media={<Image guide={guide2} />}
        mediaFirst={true}
      />

      <TwoColumnContent
        textContent={
          <>
            <h2>Prefer to dive in and code your own projects? We have you covered.</h2>
            <div className="tagline">
              <p>
                The Adafruit IO API is a versatile tool that allows users to interact with their Adafruit IO data from various programming languages and hardware environments. It provides a straightforward way to send and receive data to and from Adafruit IO feeds using HTTP or MQTT protocols. The API is designed to be accessible, making it easy for beginners to get started with Internet of Things (IoT) projects using one of many compatible devices.
              </p>
              <p>
                One of the key features of the Adafruit IO API is its support for multiple client libraries, including CircuitPython, Arduino, Python, and more. For the more advanced developers, take a look at our REST API and MQTT API.
              </p>
            </div>
          </>
        }
        media={<Image guide={guide3} />}
        mediaFirst={false}
      />

      <OneColumnContent>
        <section>
          <div>
            <h2>Built from the ground up to be easy to use.</h2>
            <p>
              We do the hard work so you can focus on the fun stuff.
            </p>
          </div>
          <div className="icon-block">
            <div>
              <img alt="WipperSnapper Icon"
                src={wippersnapper}
                loading="lazy" />
              <div>
                <h3>Powered by Wippersnapper</h3>
                <p>
                  WipperSnapper is a firmware designed to turn <IOLink to="/works-with-wippersnapper">any WiFi-capable board</IOLink> into an Internet-of-Things device without programming a single line of code.
                </p>
              </div>
            </div>
            <div>
              <img alt="Credit Card Icon"
                src={card}
                loading="lazy" />
              <div>
                <h3>Free For All</h3>
                <p>
                  The majority of the functionality of Adafruit IO is available to anyone with an Adafruit account, and is completely free!
                </p>
              </div>
            </div>
            <div>
              <img alt="Gauge Icon"
                src={gauge}
                loading="lazy" />
              <div>
                <h3>Beautiful Dashboards</h3>
                <p>
                  Data won't help you make better decisions or understand the world you're living in unless you can see it. Charts, graphs, gauges, logging, and more are available from anywhere in our carefully designed web-based dashboards.
                </p>
              </div>
            </div>
            <div>
              <img alt="Book Icon"
                src={book}
                loading="lazy" />
              <div>
                <h3>Documentation</h3>
                <p>
                  We've written a lot more about Adafruit IO than just our web API. Teaching is at the heart of Adafruit, and our Learning System has all the projects and guides you need to get started or to level up!
                </p>
              </div>
            </div>
            <div>
              <img alt="Secure Icon"
                src={secure}
                loading="lazy" />
              <div>
                <h3>Private & Secure</h3>
                <p>
                  We won't share your account information with anyone without your permission and everything you store on Adafruit IO is private by default, whether you're a paying customer or not.
                </p>
              </div>
            </div>
            <div>
              <img alt="Download Icon"
                src={download}
                loading="lazy" />
              <div>
                <h3>It’s Your Data</h3>
                <p>
                  The data you store with IO is yours to manage and control. You can download it all anytime and we will never sell or give it away to another company. We feel strongly enough about this that we put it in our IoT Bill of Rights.
                </p>
              </div>
            </div>
          </div>
        </section>
      </OneColumnContent>

      <TwoColumnContent
        textContent={
          <>
            <h2>We play nice with any device.</h2>
            <div className="tagline">
              <p>
                Adafruit IO is a versatile platform that plays nicely with a wide array of devices, making it a go-to choice for both beginners and experienced developers. Whether you're using Arduino, Raspberry Pi, ESP8266, or ESP32, Adafruit IO provides seamless integration through its APIs and libraries.
              </p>
              <p>
                Whether you're building a smart home system, a weather station, or a data logging application, Adafruit IO's compatibility with a wide range of devices and protocols ensures that you can bring your ideas to life with ease.
              </p>
            </div>
          </>
        }
        media={<Image guide={guide4} />}
        mediaFirst={true}
      />

      <OneColumnContent>
        <section className="pricing-section">
          <div>
            <h2>Our pricing is as simple as our API.</h2>
            <p>
              <b>Try Adafruit IO for Free.</b> Unlock its full potential for $10 per month.
            </p>
          </div>
          <div className="pricing-block">
            <div className="price-card free">
              <img alt="Adafruit IO Logo"
                src={io_logo_black}
                loading="lazy" />
              <div className="type">Adafruit IO Basic</div>
              <div className="price-details">
                <div>
                  <div className="price">$0</div>
                  <div className="period">forever</div>
                </div>
              </div>
              <div className="features">
                <ul>
                  <li><b>30</b> data points per minute</li>
                  <li><b>30</b> days of data storage</li>
                  <li>Actions every <b>15 minutes</b></li>
                  <li><b>5</b> dashboards</li>
                  <li><b>2</b> <IOLink to="/works-with-wippersnapper">WipperSnapper devices</IOLink></li>
                  <li><b>5</b> groups</li>
                  <li><b>10</b> feeds</li>
                </ul>
              </div>
              <div className="sign-up-link">
                {freeSignUpLink()}
              </div>
            </div>
            <div className="price-card plus">
              <img alt="Adafruit IO Plus Logo"
                src={io_plus_logo_white}
                loading="lazy" />
              <div className="type">Adafruit IO+</div>
              <div className="price-details">
                <div>
                  <div className="price">$10</div>
                  <div className="period">monthly</div>
                </div>
                <div className="separator">or</div>
                <div>
                  <div className="price">$99</div>
                  <div className="period">annually</div>
                </div>
              </div>
              <div className="features">
                <ul>
                  <li><b>60</b> data points per minute</li>
                  <li><b>60</b> days of data storage</li>
                  <li>Actions every <b>5 seconds</b></li>
                  <li><b>25</b> SMS messages per day*</li>
                  <li><b>Unlimited</b> dashboards</li>
                  <li><b>Unlimited</b> <IOLink to="/works-with-wippersnapper">WipperSnapper devices</IOLink></li>
                  <li><b>Unlimited</b> groups</li>
                  <li><b>Unlimited</b> feeds</li>
                </ul>
                <div className="disclaimer">
                  *(UTC) for actions & Messages Power-Up (USA and Canada)
                </div>
              </div>
              <div className="sign-up-link">
                {plusSignUpLink()}
              </div>
            </div>
          </div>
        </section>
      </OneColumnContent>

      <OneColumnContent>
        <section>
          <div>
            <h2>The Internet of Things for Everyone</h2>
            <p>
              Find documentation, AIO ready products, data peace of mind, and the best customer support.
            </p>
          </div>
          <div className="icon-block">
            <div>
              <img alt="Book Icon"
                src={book}
                loading="lazy" />
              <div>
                <p>
                  Don't forget that we have some really great documentation.
                </p>
                <div>
                  <ExternalLink to={ import.meta.env.VITE_DOCUMENTATION_URL } title="Adafruit IO API Docs">
                    Check out the API Docs
                  </ExternalLink>
                </div>
                <div>
                  <a onClick={ showGuides }>Check out the beginners' guides</a>
                </div>
              </div>
            </div>
            <div>
              <img alt="Microchip Icon"
                src={chip}
                loading="lazy" />
              <div>
                <p>
                  All our IoT products are ready to work with Adafruit IO.
                </p>
                <div>
                  <ExternalLink to="https://www.adafruit.com/product/4985" title="Adafruit FunHouse Product Page">
                    Adafruit FunHouse
                  </ExternalLink>
                </div>
                <div>
                  <ExternalLink to="https://www.adafruit.com/product/4800" title="Adafruit MagTag Product Page">
                    Adafruit MagTag
                  </ExternalLink>
                </div>
                <div>
                  <ExternalLink to="https://www.adafruit.com/product/2680" title="Adafruit HUZZAH32 Product Page">
                    Adafruit HUZZAH32
                  </ExternalLink>
                </div>
              </div>
            </div>
            <div>
              <img alt="Shield Icon"
                src={shield}
                loading="lazy" />
              <div>
                <p>
                  We believe Internet of Things devices should come with a well established expectation of what they will and will not do with consumer’s data.
                </p>
                <div>
                  <ExternalLink to="https://www.adafruit.com/iotbor">
                    Adafruit IoT Bill of Rights
                  </ExternalLink>
                </div>
                <div>
                  <IOLink to="terms">Our Terms of Service</IOLink>
                </div>
                <div>
                  <ExternalLink to="https://www.adafruit.com/privacy">Our Privacy Policy</ExternalLink>
                </div>
              </div>
            </div>
            <div>
              <img alt="Chat Icon"
                src={chat}
                loading="lazy" />
              <div>
                <p>
                  Still confused about all of this Internet of Things Adafruit IO Stuff?
                </p>
                <p>
                  We answer a lot of questions in our FAQ but if that's not enough you can <IOLink to="support">get in touch with us.</IOLink>
                </p>
              </div>
            </div>
          </div>
        </section>
      </OneColumnContent>
    </div>
  )
}

export default Landing
